import React from 'react';
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListLegQuery } from "store/services/legApi";
import { useAppMeta, useTablePagination } from "hooks";

const LegListScreen = () => {

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Legs" }];

  const legTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Leg ID",
      selector: (row) => row?.legId,
      cell: (row) => row?.legId,
    },
    {
      name: "Source Branch",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name,
    },
    {
      name: "Destination Branch",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/leg/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Leg" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/leg/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Leg">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchLegs, { data: legRes, isLoading, isFetching, error }] = useLazyListLegQuery();

  React.useEffect(() => {
    fetchLegs({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchLegs, keyword, perPage]);

  useAppMeta(`${activeTab} Legs`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={legTabs} activeTab={activeTab} />

        <TableFilter searchLabel="Leg ID" disabled={isLoading || isFetching}>
          <div className="font-medium">{activeTab} Legs</div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={legRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationPerPage={perPage}
          paginationTotalRows={legRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default LegListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/leg/create">
      <Button startIcon={<Add />} variant="contained" color="primary">
        Add Leg
      </Button>
    </Link>
  );
};
